import qs from "qs"
import { Fragment, useMemo, useState, useCallback } from "react"
import { EyeIcon, LoaderCircleIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react"
import { PopoverClose } from "@radix-ui/react-popover"
import { Outlet, useFetcher, useRouteLoaderData, useLocation, Link, useSearchParams } from "react-router"
import DOMPurify from "dompurify"
import { getOrganisations } from "@/utils/projects"
import { fetchAPI, handleResponse } from "@/utils"
import { FiltersWrapper, type StoredSession, type OnBeforeSubmit } from "@/components/Filters"
import type { RootLoader } from "../root"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { hasPermission } from "@/machines/Authmachine"
import Pagination from "@/components/ui/pagination"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

const routeId = "guidance-routeId"
const sessionKey = "guidance-filter"

export interface Guidance {
  active: boolean
  code: string
  name: string
  organisation: string
  value: string
}

interface GuidanceAPIResponse {
  items: Guidance[]
  page: number
  totalCount: number
}

interface DeleteActionResponse {
  errorMessage?: string
  validationErrors?: Array<{ field: string; message: string }>
}

async function loader({ request }: { request: Request }) {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const url = new URL(request.url)
  const page = +(url.searchParams.get("page") ?? 1)
  const pageSize = +(url.searchParams.get("pageSize") ?? 10)
  const sessionData = sessionStorage.getItem(sessionKey)

  const filters =
    sessionData !== null && sessionData.length > 0 ? (qs.parse(sessionData) as unknown as Partial<StoredSession>) : {}
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  try {
    const response = await fetchAPI<GuidanceAPIResponse>(
      `/api/UnityReview/Guidance/${loadedOrganisation}/${page}/${pageSize}`,
    ).then(handleResponse)

    return {
      guidance: response.items,
      loadedOrganisation,
      totalCount: response.totalCount,
      page: response.page,
    }
  } catch (error) {
    if (error !== null && typeof error === "object" && "status" in error && error.status === 404) {
      return { guidance: [], loadedOrganisation, totalCount: 0, page: 1 }
    }
    throw error
  }
}

async function mainAction({ request }: { request: Request }): Promise<DeleteActionResponse> {
  const formData = await request.formData()
  const action = formData.get("action") as string | null
  const organisation = formData.get("organisation") as string | null

  if (organisation === null || organisation.length === 0) {
    return {
      errorMessage: "Missing organisation",
      validationErrors: [{ field: "organisation", message: "Organisation is required" }],
    }
  }

  let errorMessage: string | undefined
  const validationErrors: Array<{ field: string; message: string }> = []

  switch (action) {
    case "DELETE": {
      const name = formData.get("name") as string | null

      if (name === null || name.length === 0) {
        errorMessage = "Missing guidance name"
        validationErrors.push({ field: "name", message: "Guidance name is required" })
        break
      }

      try {
        const response = await fetchAPI(`/api/UnityReview/Guidance/${organisation}/${name}`, {
          method: "DELETE",
        })

        if (!response.ok) {
          const errorData = (await response.json().catch(() => null)) as { title?: string } | null
          errorMessage = errorData?.title ?? "Error deleting guidance"
        }
      } catch {
        errorMessage = "Error deleting guidance"
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { errorMessage, validationErrors: validationErrors.length > 0 ? validationErrors : undefined }
}

const truncateToWords = (text: string, wordCount = 10): string => {
  const words = text.trim().split(/\s+/)
  if (words.length <= wordCount) return text
  return words.slice(0, wordCount).join(" ") + "..."
}

function Main() {
  const fetcher = useFetcher<DeleteActionResponse>()
  const data = useRouteLoaderData<typeof loader>(routeId) // ✅ Changed to useRouteLoaderData
  const url = new URL(window.location.href)
  const page = +(url.searchParams.get("page") ?? 1)
  const pageSize = +(url.searchParams.get("pageSize") ?? 10)

  const pagingAdjustment = (page - 1) * pageSize

  // Safety check
  if (data == null) {
    return <div className="grid h-screen place-items-center">Loading...</div>
  }

  return (
    <div className="h-screen overflow-y-auto">
      <Pagination routeId={routeId} />

      {fetcher.data?.errorMessage != null && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
          <div className="text-sm">{fetcher.data.errorMessage}</div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12" />
            <TableHead className="w-24 text-left lg:w-32 xl:w-64">Name</TableHead>
            <TableHead className="w-24 text-left lg:w-32 xl:w-64">Code</TableHead>
            <TableHead className="text-left">Value</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.guidance.length > 0 ? (
            data.guidance.map((item, index) => (
              <TableRow key={`${item.organisation}-${item.name}`} className="text-sm">
                <TableCell>{index + 1 + pagingAdjustment}.</TableCell>
                <TableCell className="break-words whitespace-normal">{item.name}</TableCell>
                <TableCell className="break-words whitespace-normal">{item.code}</TableCell>
                <TableCell>
                  <div className="line-clamp-2 max-w-md">{truncateToWords(item.value.replace(/<[^>]*>/g, ""), 10)}</div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <GuidancePreview guidance={item} />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button asChild variant="ghost" size="icon">
                          <Link to={`${item.name}/edit?page=${page}&pageSize=${pageSize}`}>
                            <PencilIcon className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit Guidance</TooltipContent>
                    </Tooltip>
                    <Popover>
                      <Tooltip>
                        <PopoverTrigger asChild>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-600 hover:text-red-700 disabled:cursor-not-allowed"
                            >
                              <Trash2Icon className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                        </PopoverTrigger>
                        <TooltipContent>Delete Guidance</TooltipContent>
                      </Tooltip>
                      <PopoverContent side="right" align="start" className="max-w-sm">
                        <p className="text-sm text-pretty">
                          Guidance &quot;<span className="font-semibold">{item.name}</span>&quot; will be deleted. Are
                          you sure?
                        </p>
                        <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
                          <input type="hidden" name="action" value="DELETE" />
                          <input type="hidden" name="organisation" value={data.loadedOrganisation} />
                          <input type="hidden" name="name" value={item.name} />
                          {fetcher.state !== "idle" && fetcher.formData?.get("name") === item.name && (
                            <span className="flex items-center justify-start gap-1 pr-4">
                              <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
                            </span>
                          )}
                          <div className="flex-1" />
                          <PopoverClose asChild>
                            <Button variant="ghost">Cancel</Button>
                          </PopoverClose>
                          <Button type="submit" variant="destructive">
                            Confirm
                          </Button>
                        </fetcher.Form>
                      </PopoverContent>
                    </Popover>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="py-4 text-center text-gray-500">
                No guidance found for this organisation.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Outlet context={{ loadedOrganisation: data.loadedOrganisation, guidanceItems: data.guidance }} />
    </div>
  )
}

function GuidancePreview({ guidance }: { guidance: Guidance }) {
  const [isOpen, setIsOpen] = useState(false)
  const sanitizedValue = useMemo(() => DOMPurify.sanitize(guidance.value), [guidance.value])

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <EyeIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Preview Guidance</TooltipContent>
      </Tooltip>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="break-words break-all">{guidance.name}</DialogTitle>
          <DialogDescription>Code: {guidance.code}</DialogDescription>
        </DialogHeader>
        <div
          className="text-base-content max-h-96 overflow-y-auto rounded-md border p-4 text-sm break-words whitespace-pre-wrap"
          dangerouslySetInnerHTML={{ __html: sanitizedValue }}
        />
        <div className="flex justify-end">
          <DialogClose asChild>
            <Button variant="ghost">Close</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function Guidance() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const page = +(searchParams.get("page") ?? 1)
  const pageSize = +(searchParams.get("pageSize") ?? 10)
  const isFormPage = location.pathname.includes("/new") || location.pathname.includes("/edit")
  const organisations = rootData?.organisations ?? []
  const hasOrganisations = organisations.length > 0
  const loadedOrg = data?.loadedOrganisation
  const defaultOrganisation =
    loadedOrg != null && loadedOrg.length > 0 ? loadedOrg : hasOrganisations ? organisations[0] : ""
  const canWrite = hasPermission("writeAccess")

  const onBeforeSubmit = useCallback<OnBeforeSubmit>(
    (formData, reason) => {
      if (reason.type !== "field") return
      if (reason.name === "organisation") {
        formData.delete("page")
        setSearchParams((params: URLSearchParams) => {
          params.delete("page")
          return params
        })
      }
    },
    [setSearchParams],
  )

  return (
    <Fragment>
      {!isFormPage && (
        <div className="flex items-center gap-4">
          {hasOrganisations && (
            <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
              <FiltersWrapper.Select
                name="organisation"
                label="Organisation"
                defaultValue={defaultOrganisation}
                items={organisations}
              />
            </FiltersWrapper>
          )}
          <div className="flex-1" />
          {canWrite && (
            <Button asChild variant="outline">
              <Link to={`new?page=${page}&pageSize=${pageSize}`}>
                <PlusIcon className="mr-2 h-4 w-4" /> Add New
              </Link>
            </Button>
          )}
        </div>
      )}
      <Outlet context={{ loadedOrganisation: data?.loadedOrganisation, guidanceItems: data?.guidance ?? [] }} />
    </Fragment>
  )
}

Guidance.Main = Main
Guidance.loader = loader
Guidance.routeId = routeId

Main.action = mainAction
